import { useEffect, useRef, useState } from 'react';

interface ContextMenuPosition {
  mouseX: number | null;
  mouseY: number | null;
}

const initialPosition: ContextMenuPosition = {
  mouseX: null,
  mouseY: null,
};

interface UseCardContextMenuReturn<T> {
  contextMenuPosition: ContextMenuPosition;
  contextMenuItem: T | undefined;
  menuPaperRef: React.RefObject<HTMLDivElement>;
  handleContextMenuOpen: (event: React.MouseEvent, item: T) => void;
  handleContextMenuClose: () => void;
}

/**
 * Hook que encapsula a lógica de menu de contexto (botão direito) para listas de cards.
 * Gerencia posição do menu, item alvo, abertura/fechamento e listeners de teclado/mouse.
 *
 * Comportamento:
 * - Botão direito em cima de um card abre o menu na posição do cursor.
 * - Botão direito em outro card reposiciona o menu sem fechar e reabrir.
 * - Clique esquerdo fora do Paper fecha o menu.
 * - Tecla Escape fecha o menu.
 */
function useCardContextMenu<T>(): UseCardContextMenuReturn<T> {
  const [contextMenuPosition, setContextMenuPosition] =
    useState<ContextMenuPosition>(initialPosition);
  const [contextMenuItem, setContextMenuItem] = useState<T | undefined>(
    undefined,
  );
  const menuPaperRef = useRef<HTMLDivElement>(null);

  const handleContextMenuOpen = (event: React.MouseEvent, item: T) => {
    event.preventDefault();
    setContextMenuItem(item);
    setContextMenuPosition({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleContextMenuClose = () => {
    setContextMenuPosition(initialPosition);
  };

  // Fecha o menu ao clicar com o botão esquerdo fora do Paper, ao pressionar Escape ou ao rolar a tela.
  // Botão direito (button=2) é ignorado para que o onContextMenu de outro card
  // reposicione o menu sem fechar e reabrir.
  useEffect(() => {
    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (e.button === 2) return;
      if (menuPaperRef.current?.contains(e.target as Node)) return;
      handleContextMenuClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleContextMenuClose();
    };

    const handleScroll = () => {
      handleContextMenuClose();
    };

    if (contextMenuPosition.mouseY !== null) {
      document.addEventListener('mousedown', handleDocumentMouseDown);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('scroll', handleScroll, {
        capture: true,
        passive: true,
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [contextMenuPosition.mouseY]);

  return {
    contextMenuPosition,
    contextMenuItem,
    menuPaperRef,
    handleContextMenuOpen,
    handleContextMenuClose,
  };
}

export default useCardContextMenu;
